/**
 * Centralized Logging Utility
 * Replaces console.log/error/warn with environment-aware logging
 */

const LOG_LEVELS = {
  NONE: 0,
  ERROR: 1,
  WARN: 2,
  INFO: 3,
  DEBUG: 4
};

// Get log level from environment or default to INFO in production, DEBUG in development
const getLogLevel = () => {
  if (typeof window === 'undefined') return LOG_LEVELS.INFO;
  
  const envLevel = window.__ADMIN_LOG_LEVEL__;
  if (envLevel !== undefined) return parseInt(envLevel, 10);
  
  // Default to DEBUG in development, INFO in production
  return process.env.NODE_ENV === 'development' ? LOG_LEVELS.DEBUG : LOG_LEVELS.INFO;
};

const currentLogLevel = getLogLevel();

/**
 * Logger class for centralized logging
 */
class Logger {
  constructor(prefix = 'Admin') {
    this.prefix = prefix;
  }

  /**
   * Log error message
   * @param {string} message - Error message
   * @param {Error|Object} [error] - Error object or additional data
   */
  error(message, error = null) {
    if (currentLogLevel >= LOG_LEVELS.ERROR) {
      console.error(`[${this.prefix}] ERROR:`, message, error || '');
    }
  }

  /**
   * Log warning message
   * @param {string} message - Warning message
   * @param {Object} [data] - Additional data
   */
  warn(message, data = null) {
    if (currentLogLevel >= LOG_LEVELS.WARN) {
      console.warn(`[${this.prefix}] WARN:`, message, data || '');
    }
  }

  /**
   * Log info message
   * @param {string} message - Info message
   * @param {Object} [data] - Additional data
   */
  info(message, data = null) {
    if (currentLogLevel >= LOG_LEVELS.INFO) {
      console.log(`[${this.prefix}] INFO:`, message, data || '');
    }
  }

  /**
   * Log debug message (only in development)
   * @param {string} message - Debug message
   * @param {Object} [data] - Additional data
   */
  debug(message, data = null) {
    if (currentLogLevel >= LOG_LEVELS.DEBUG) {
      console.log(`[${this.prefix}] DEBUG:`, message, data || '');
    }
  }

  /**
   * Create a child logger with a specific prefix
   * @param {string} prefix - Child logger prefix
   * @returns {Logger} - New logger instance
   */
  child(prefix) {
    return new Logger(`${this.prefix}:${prefix}`);
  }
}

// Export default logger and create specialized loggers
const logger = new Logger('Admin');

export default logger;

// Specialized loggers for different modules
export const analyticsLogger = logger.child('Analytics');
export const firebaseLogger = logger.child('Firebase');
export const chartLogger = logger.child('Chart');
export const serviceLogger = logger.child('Service');

// Export log levels for configuration
export { LOG_LEVELS };

